import { fromJS, List, Map, OrderedMap } from 'immutable';
import getIn from 'lodash/get';
import { LOCATION_CHANGE } from 'react-router-redux';

import * as PodcastConstants from './constants';
import * as D from './deps';

export const INITIAL_STATE = Map({
  [PodcastConstants.PodcastKeys.PROGRESS]: OrderedMap(),
  [PodcastConstants.PodcastKeys.NEW_EPISODE_BADGE]: OrderedMap(),
  [PodcastConstants.PodcastKeys.USER_PROGRAM_DETAILS]: OrderedMap(),
  [PodcastConstants.PodcastKeys.EPISODES_WITH_LABELS]: List(),
  [PodcastConstants.PodcastKeys.THUMBED_EPISODES]: List(),
  [PodcastConstants.PodcastKeys.ACTIVE_BACKSTAGE_PROGRAM_ID]: null,
});

export default D.createReducer(INITIAL_STATE, {
  [PodcastConstants.ActionTypes.SET_ACTIVE_BACKSTAGE_PROGRAM_ID]: setActiveBackstageProgramId,
  [D.Constants.Api.ApiActions.AESOP_ANNOTATE_OBJECTS_SUCCESS]: onAnnotateObjectsSuccess,
  [D.Constants.Api.ApiActions.AESOP_GET_DETAILS_SUCCESS]: onGetDetailsSuccess,
  [D.Constants.Api.ApiActions.AESOP_GET_ALL_EPISODES_BY_PROGRAM_SUCCESS]: onGetAllEpisodesSuccess,
  [PodcastConstants.ActionTypes.ADD_EPISODE_TO_THUMBS_LIST]: addEpisodeToThumbsList,
  [PodcastConstants.ActionTypes.REMOVE_EPISODE_FROM_THUMBS_LIST]: removeEpisodeFromThumbsList,
  [PodcastConstants.ActionTypes.UPDATE_PODCAST_EPISODE_PROGRESS]: updatePodcastEpisodeProgress,
  [D.Constants.Api.ApiActions.AESOP_GET_ALL_THUMBED_EPISODES_BY_PROGRAM_SUCCESS]:
    onGetAllThumbedEpisodesSuccess,
  [D.Constants.Api.ApiActions.AESOP_REMOVE_EPISODES_FEEDBACK_SUCCESS]:
    onRemoveEpisodesFeedbackSuccess,
  [LOCATION_CHANGE]: updatePodcastBackstageVisits,
});

function createUserProgramDetails(response) {
  const { numThumbsUp, numThumbsDown, continueListeningEpisode, sortingOrder } = response;
  const podcastId = getIn(response, 'details.podcastProgramDetails.pandoraId');
  const recentEpisodes = getIn(response, 'details.podcastProgramDetails.recentEpisodes');
  if (!podcastId) return {};

  return {
    [podcastId]: {
      numThumbsUp,
      numThumbsDown,
      continueListeningEpisode,
      sortingOrder,
      recentEpisodes: {
        [sortingOrder]: recentEpisodes,
      },
    },
  };
}

function setActiveBackstageProgramId(state, action) {
  const { programId } = action.data;

  if (programId) {
    return state.setIn([PodcastConstants.PodcastKeys.ACTIVE_BACKSTAGE_PROGRAM_ID], programId);
  }

  return state;
}

// We want to store in here the items that aren't in the entities/catalog reducer
function onAnnotateObjectsSuccess(state, action) {
  const response = action.response;
  if (response) {
    const { progress = {}, newEpisodeBadge = {} } = response;

    return state.mergeDeep({
      [PodcastConstants.PodcastKeys.PROGRESS]: progress,
      [PodcastConstants.PodcastKeys.NEW_EPISODE_BADGE]: newEpisodeBadge,
    });
  }
  return state;
}

function onGetDetailsSuccess(state, action) {
  const response = action.response;

  if (response) {
    const { progress = {} } = response;

    return state.mergeDeep({
      [PodcastConstants.PodcastKeys.PROGRESS]: progress,
      [PodcastConstants.PodcastKeys.USER_PROGRAM_DETAILS]: createUserProgramDetails(response),
    });
  }
  return state;
}

function onGetAllEpisodesSuccess(state, action) {
  const pandoraId = getIn(action, 'params.content.pandoraId');
  const sortingOrder = getIn(action, 'response.sortingOrder');
  const episodesWithLabel = getIn(action, 'response.episodes.episodesWithLabel');

  if (pandoraId && episodesWithLabel) {
    return state
      .setIn([PodcastConstants.PodcastKeys.EPISODES_WITH_LABELS], fromJS(episodesWithLabel))
      .setIn([PodcastConstants.PodcastKeys.ACTIVE_BACKSTAGE_PROGRAM_ID], pandoraId)
      .setIn(
        [PodcastConstants.PodcastKeys.USER_PROGRAM_DETAILS, pandoraId, 'sortingOrder'],
        sortingOrder,
      );
  }
  return state;
}

function onGetAllThumbedEpisodesSuccess(state, action) {
  const response = action.response;
  const pandoraId = getIn(action, 'params.content.pandoraId');

  if (response) {
    const { progress = {}, episodeList = [] } = response;

    return state
      .mergeDeep({ [PodcastConstants.PodcastKeys.PROGRESS]: progress })
      .setIn([PodcastConstants.PodcastKeys.THUMBED_EPISODES], List(episodeList))
      .setIn([PodcastConstants.PodcastKeys.ACTIVE_BACKSTAGE_PROGRAM_ID], pandoraId);
  }

  return state;
}

function onRemoveEpisodesFeedbackSuccess(state, action) {
  const response = action.response;
  if (response) {
    const { progress = {}, removed = [] } = response;

    // the aesop removeFeedback api only accepts an array of episode ids for convenience. in practice though,
    // we'll only be removing one episode per request since our UI doesn't have a bulk thumb removal feature
    // so for now, it should be ok to just get the first item of the list.
    const removedEpisodeId = removed[0];
    const episodesList = state.getIn([PodcastConstants.PodcastKeys.THUMBED_EPISODES], List());
    return state.mergeDeep({ [PodcastConstants.PodcastKeys.PROGRESS]: progress }).setIn(
      [PodcastConstants.PodcastKeys.THUMBED_EPISODES],
      episodesList.filter(id => id !== removedEpisodeId),
    );
  }

  return state;
}

function addEpisodeToThumbsList(state, action) {
  const { episodeId } = action.data;

  if (episodeId) {
    const episodesList = state.getIn([PodcastConstants.PodcastKeys.THUMBED_EPISODES], List());
    return state.setIn(
      [PodcastConstants.PodcastKeys.THUMBED_EPISODES],
      episodesList.unshift(episodeId),
    );
  }

  return state;
}

function removeEpisodeFromThumbsList(state, action) {
  const { episodeId } = action.data;

  if (episodeId) {
    const episodesList = state.getIn([PodcastConstants.PodcastKeys.THUMBED_EPISODES], List());
    return state.setIn(
      [PodcastConstants.PodcastKeys.THUMBED_EPISODES],
      episodesList.filter(id => id !== episodeId),
    );
  }

  return state;
}

function updatePodcastEpisodeProgress(state, action) {
  const pandoraId = getIn(action, 'data.pandoraId');
  const elapsedTime = getIn(action, 'data.elapsedTime', 0);
  const isFinished = getIn(action, 'data.isFinished');

  if (pandoraId) {
    return state
      .setIn([PodcastConstants.PodcastKeys.PROGRESS, pandoraId, 'elapsedTime'], elapsedTime)
      .setIn([PodcastConstants.PodcastKeys.PROGRESS, pandoraId, 'isFinished'], isFinished);
  }

  return state;
}

function updatePodcastBackstageVisits(state, action) {
  const path = getIn(action, 'payload.pathname');

  if (D.Utils.Route.isBackstagePodcastProgramPath(path)) {
    const programId = path.split('/')[3];
    return state.setIn(
      [PodcastConstants.PodcastKeys.NEW_EPISODE_BADGE, programId, 'backstageVisited'],
      true,
    );
  }

  return state;
}
